import { useEffect, useRef, useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { Redirect, useHistory } from 'react-router-dom';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  clearInviteState,
  getInviteState,
  getInviteToken,
  isWalletInvitationFlowPath,
  shouldInvalidatePersistedInviteState,
  useWalletInvitation,
} from 'src/utils/hooks/walletInvitation';

import { WalletInvitationRedirectModal } from './WalletInvitationRedirectModal';

import type { WalletInvitationTokenData } from 'src/services/invite-monitor';

import Register from './index';

const INVITE_REGISTER_FIRST_STEP_BASE =
  '/cadastro/identificacao?type=private_person';

/**
 * Componente intermediário que verifica se o usuário já está logado
 * e se há um token de convite na URL.
 * Se sim, processa o token e redireciona para o monitor.
 * Se não, segue o fluxo normal.
 */
export default function RegisterRedirect() {
  const { auth } = useAuthContext();
  const history = useHistory();
  const shouldInvalidateStoredInviteState =
    shouldInvalidatePersistedInviteState(
      history.location.pathname ?? '',
      history.location.search ?? '',
    );

  if (shouldInvalidateStoredInviteState) {
    clearInviteState();
  }

  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [confirmedData, setConfirmedData] =
    useState<WalletInvitationTokenData | null>(null);
  const hasConfirmed = useRef(false);
  const hasRedirectedToRegister = useRef(false);

  const {
    token,
    isLoading,
    data,
    confirm,
    isLoadingConfirm,
    canConfirmWalletInvitation,
    hasInvitationEmailMismatch,
    invitationEmail,
    authenticatedEmail,
  } = useWalletInvitation({
    onConfirmSuccess: (responseData) => {
      setConfirmedData(responseData);
      setShowRedirectModal(true);
    },
  });

  const shouldShowMismatchGuidance =
    auth.isLoggedIn &&
    hasInvitationEmailMismatch &&
    !!invitationEmail &&
    !!authenticatedEmail;

  useEffect(() => {
    if (
      auth.isLoggedIn &&
      token &&
      data &&
      canConfirmWalletInvitation &&
      !isLoading &&
      !isLoadingConfirm &&
      !hasConfirmed.current
    ) {
      hasConfirmed.current = true;
      confirm(token);
    }
  }, [
    auth.isLoggedIn,
    token,
    data,
    canConfirmWalletInvitation,
    isLoading,
    isLoadingConfirm,
    confirm,
  ]);

  useEffect(() => {
    const unlisten = history.listen((nextLocation) => {
      if (isWalletInvitationFlowPath(nextLocation.pathname)) {
        return;
      }

      if (!getInviteToken() && !getInviteState()) {
        return;
      }

      clearInviteState();
    });

    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    if (
      auth.isLoggedIn ||
      !token ||
      !data ||
      isLoading ||
      isLoadingConfirm ||
      hasRedirectedToRegister.current
    ) {
      return;
    }

    hasRedirectedToRegister.current = true;
    history.replace(`${INVITE_REGISTER_FIRST_STEP_BASE}&t=${token}`);
  }, [auth.isLoggedIn, token, data, history, isLoading, isLoadingConfirm]);

  const handleRedirect = () => {
    if (confirmedData?.owner?.id) {
      window.location.href = `/monitor/${confirmedData.owner.id}?tab=accepted`;
    }
  };

  if (auth.isLoggedIn && token && (isLoading || isLoadingConfirm)) {
    return <CircularProgress />;
  }

  if (auth.isLoggedIn && !token) {
    return <Redirect to="/plataforma" />;
  }

  if (shouldShowMismatchGuidance) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', px: 2, pt: 12 }}>
        <Typography
          data-testid="invite-email-mismatch-message"
          sx={{ color: 'error.main' }}>
          Este convite foi enviado para <b>{invitationEmail}</b>, mas você está
          logado com <b>{authenticatedEmail}</b>. Para aceitar o convite, troque
          de conta, faça login com o e-mail convidado ou crie uma nova conta com
          ele.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Register />
      <WalletInvitationRedirectModal
        open={showRedirectModal}
        ownerId={String(confirmedData?.owner?.id ?? '')}
        onRedirect={handleRedirect}
      />
    </>
  );
}
